// The `ValidationError` type and the checks that need only a field's value. This file doesn't import
// `nodes/registry`, so node types can import it without an import cycle.

import type { TextTemplateChunk } from '@/proto/logiblocks/logiblocks';
import { MSG_REQUIRED } from './validationMessages';

/**
 * Categories of validation errors for programmatic handling.
 */
export type ValidationErrorType =
  | 'REQUIRED_FIELD' // Field is empty/missing (string, array, variable ref)
  | 'VARIABLE_ERROR' // Variable ref issues (not ancestor, unknown variable, unknown type)
  | 'SYNTAX_ERROR' // Template syntax (malformed {{...}}, unclosed braces)
  | 'VALUE_ERROR' // Value doesn't match expected type (invalid alert type)
  | 'UNIMPLEMENTED_NODE'; // Node type not yet implemented

/**
 * A validation error with a type for programmatic handling and a message for display.
 */
export interface ValidationError {
  /** The category/type of the validation error */
  type: ValidationErrorType;
  /** The field that has the error (e.g., "Recipient teams", "Message template") */
  field: string;
  /** Human-readable error message for display to users */
  message: string;
  /** Optional, ephemeral grouping key for the publish control's unresolved-
   * field count. This is presentation metadata, not persistent error identity. */
  displayGroupKey?: string;
}

export interface UnresolvedFieldSummary {
  unresolvedFieldCount: number;
  affectedNodeIds: string[];
  firstAffectedNodeId: string | undefined;
}

/** Groups raw validation messages into user-visible fields without changing
 * the raw error map that remains authoritative for publish blocking. */
export function getUnresolvedFieldSummary(
  validationErrors: ReadonlyMap<string, readonly ValidationError[]>
): UnresolvedFieldSummary {
  let unresolvedFieldCount = 0;
  const affectedNodeIds: string[] = [];

  validationErrors.forEach((errors, nodeId) => {
    if (errors.length === 0) return;
    affectedNodeIds.push(nodeId);
    const groups = new Set(
      errors.map((error) =>
        error.displayGroupKey === undefined
          ? `field:${JSON.stringify(error.field)}`
          : `explicit:${JSON.stringify(error.displayGroupKey)}`
      )
    );
    unresolvedFieldCount += groups.size;
  });

  return {
    unresolvedFieldCount,
    affectedNodeIds,
    firstAffectedNodeId: affectedNodeIds[0],
  };
}

/**
 * Validates that a required string field is not empty.
 *
 * @param value - The string value to validate
 * @param fieldName - Human-readable field name for error messages
 * @returns A ValidationError if validation fails (empty/whitespace), undefined if valid
 */
export function validateRequiredString(
  value: string | undefined,
  fieldName: string
): ValidationError | undefined {
  if (!value || value.trim() === '') {
    return {
      type: 'REQUIRED_FIELD',
      field: fieldName,
      message: MSG_REQUIRED,
    };
  }
  return undefined;
}

/**
 * Whether the chunks hold anything besides whitespace text: a variable, a
 * mention, or text with a visible character.
 */
export function hasTemplateContent(chunks: TextTemplateChunk[]): boolean {
  for (const chunk of chunks) {
    if (chunk.text === undefined || chunk.text.trim() !== '') {
      return true;
    }
  }
  return false;
}

/**
 * Validates that a required template field's chunks have something in them. A
 * variable or a mention counts; text counts unless it's only whitespace.
 */
export function validateRequiredTemplate(
  templateChunks: TextTemplateChunk[],
  fieldName: string
): ValidationError | undefined {
  if (hasTemplateContent(templateChunks)) {
    return undefined;
  }
  return { type: 'REQUIRED_FIELD', field: fieldName, message: MSG_REQUIRED };
}

/**
 * Validates that a required array field is not empty.
 *
 * @param value - The array to validate
 * @param fieldName - Human-readable field name for error messages
 * @returns A ValidationError if validation fails (undefined/empty), undefined if valid
 */
export function validateRequiredArray<T>(
  value: T[] | undefined,
  fieldName: string
): ValidationError | undefined {
  if (!value || value.length === 0) {
    return {
      type: 'REQUIRED_FIELD',
      field: fieldName,
      message: MSG_REQUIRED,
    };
  }
  return undefined;
}

/**
 * Collects non-undefined errors into an array.
 *
 * @param errors - Variable number of ValidationErrors or undefined values
 * @returns Array of ValidationErrors if any errors exist, undefined if all inputs were undefined
 */
export function collectErrors(
  ...errors: (ValidationError | undefined)[]
): ValidationError[] | undefined {
  const filtered = errors.filter((e): e is ValidationError => e !== undefined);
  return filtered.length > 0 ? filtered : undefined;
}

/** Field label for user variable validation errors. */
export const USER_VARIABLES_FIELD = 'User variables';
